/**
 * THE JR STAFF SIDE OF THE STUDENTS AND TASKS READS (B2 on the 2026-10-24
 * not-live list). Bobbie, 2026-08-23 and again 2026-08-28: a Jr Staff sees
 * dancer NAMES studio-wide and full detail only for her own classes, and only
 * the to-dos assigned to her. The rules no longer let an 'ensemble' account
 * read the students collection or the studio-wide task board, so this file is
 * how the app asks the jrStaffReads Netlify Function instead
 * (netlify/functions/lib/jrStaffReads.mjs carries the reasoning and is the
 * access boundary).
 *
 * ONE STUDENTS FETCH PER SIGNED-IN USER PER READ EPOCH, shared by every screen
 * through one module store (the rosterPassClient.ts shape). It is a snapshot,
 * not a live listener: a roster change made elsewhere reaches her on the next
 * page load or the next "Try again" (lib/readEpoch.ts), which bumps the epoch.
 * The adult staff roles keep their live listener, untouched.
 *
 * Firebase loads dynamically, like rosterPassClient.ts, so a render-only test
 * harness that mocks ./firebase never trips over it at module load.
 */
import { useEffect, useSyncExternalStore } from 'react';
// A namespace import on purpose: several render harnesses swap
// firebase/firestore for a small fake that exports only what the module under
// test uses, and a named import of anything else would stop the whole page
// loading. Looked up at call time, a missing export only matters if it is used.
import * as firestoreSdk from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { getReadEpoch, useReadEpoch } from './readEpoch';

export type JrStaffStudentDetail = 'full' | 'name';

export interface JrStaffStudentRow {
  id: string;
  detail: JrStaffStudentDetail;
  data: Record<string, unknown>;
}

interface JrStaffStudentsSnapshot {
  rows: JrStaffStudentRow[];
  loading: boolean;
  error: string | null;
  /** True once a server answer for THIS key has landed. */
  confirmed: boolean;
}

const INITIAL: JrStaffStudentsSnapshot = { rows: [], loading: true, error: null, confirmed: false };

/**
 * The server sends every Firestore Timestamp as { __ts: [seconds, nanos] }
 * (encodeJrStaffReadValue). Rebuild real Timestamps at any depth so every
 * tsToDate / tsToMillis guard on the screens keeps working on what arrives.
 */
export function decodeJrStaffReadValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(decodeJrStaffReadValue);
  if (value && typeof value === 'object') {
    const ts = (value as { __ts?: unknown }).__ts;
    if (
      Array.isArray(ts) && ts.length === 2
      && typeof ts[0] === 'number' && typeof ts[1] === 'number'
      && Object.keys(value).length === 1
    ) {
      return new firestoreSdk.Timestamp(ts[0], ts[1]);
    }
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value)) out[key] = decodeJrStaffReadValue(inner);
    return out;
  }
  return value;
}

export async function callJrStaffReads<T>(body: Record<string, unknown>): Promise<T> {
  const [{ getAuth }, { functionHeaders }] = await Promise.all([
    import('firebase/auth'),
    import('./functionSecurity'),
  ]);
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/jrStaffReads', {
    method: 'POST',
    headers: await functionHeaders(user),
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok || data.error) {
    throw jrStaffReadsFailure(data.error);
  }
  return data;
}

/**
 * The plain-words error a screen shows, carrying the lane's own code so a
 * caller can tell a refusal it expects (not_your_class) from a fault.
 */
export function jrStaffReadsFailure(code: unknown): Error & { code: string } {
  const error = new Error(code === 'too_many_requests'
    ? 'Too many tries just now. Wait a minute and try again.'
    : code === 'booking_changed'
      // The audience stamp found the booking moved to another class while it
      // worked (final review on B2): nothing was written.
      ? 'This booking just changed. Try again.'
      : 'Could not load the dancer list right now.') as Error & { code: string };
  error.code = typeof code === 'string' && code ? code : 'jr_staff_reads_failed';
  return error;
}

// ---------------------------------------------------------------------------
// The students store, keyed by `${uid}:${epoch}` so a different sign-in on the
// same device, or a Try again, never reuses an older answer.
// ---------------------------------------------------------------------------

let currentKey = '';
let snapshot: JrStaffStudentsSnapshot = INITIAL;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(key: string, next: JrStaffStudentsSnapshot): void {
  if (key !== currentKey) return;
  snapshot = next;
  for (const listener of Array.from(listeners)) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): JrStaffStudentsSnapshot {
  return snapshot;
}

function load(key: string): void {
  if (key === currentKey && (inflight || snapshot.confirmed)) return;
  currentKey = key;
  snapshot = { rows: [], loading: true, error: null, confirmed: false };
  inflight = callJrStaffReads<{ students?: unknown }>({ action: 'students' })
    .then((data) => {
      const rows: JrStaffStudentRow[] = [];
      for (const raw of Array.isArray(data.students) ? data.students : []) {
        const row = raw as { id?: unknown; detail?: unknown; data?: unknown };
        if (typeof row.id !== 'string' || !row.id) continue;
        rows.push({
          id: row.id,
          detail: row.detail === 'full' ? 'full' : 'name',
          data: (decodeJrStaffReadValue(row.data ?? {}) as Record<string, unknown>) ?? {},
        });
      }
      publish(key, { rows, loading: false, error: null, confirmed: true });
    })
    .catch((error: unknown) => {
      publish(key, {
        rows: [],
        loading: false,
        error: error instanceof Error ? error.message : 'Could not load the dancer list right now.',
        confirmed: false,
      });
    })
    .finally(() => {
      if (key === currentKey) inflight = null;
    });
  for (const listener of Array.from(listeners)) listener();
}

/** The dancers a Jr Staff may see, as raw rows. `uid` null disables the fetch. */
export function useJrStaffStudentRows(uid: string | null | undefined): JrStaffStudentsSnapshot {
  const epoch = useReadEpoch();
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const key = uid ? `${uid}:${epoch}` : '';
  useEffect(() => {
    if (key) load(key);
  }, [key]);
  if (!key) return { rows: [], loading: false, error: null, confirmed: false };
  if (key !== currentKey) return INITIAL;
  return snap;
}

/** For one-shot callers outside React (a write computing a roster). */
export async function loadJrStaffStudentRows(uid: string): Promise<JrStaffStudentRow[]> {
  const key = `${uid}:${getReadEpoch()}`;
  load(key);
  if (inflight) await inflight;
  if (snapshot.error) throw new Error(snapshot.error);
  return snapshot.rows;
}

/** The merged family uids of one class's roster, for a recipient snapshot. */
export async function loadJrStaffClassFamilyUids(offeringId: string): Promise<string[]> {
  const data = await callJrStaffReads<{ familyUids?: unknown }>({ action: 'classFamilyUids', offeringId });
  return Array.isArray(data.familyUids)
    ? data.familyUids.filter((uid): uid is string => typeof uid === 'string' && !!uid)
    : [];
}

/** Her other sign-in's to-dos, raw, with Timestamps rebuilt. */
export async function loadJrStaffAliasTasks(): Promise<Array<{ id: string; data: Record<string, unknown> }>> {
  const data = await callJrStaffReads<{ tasks?: unknown }>({ action: 'aliasTasks' });
  const out: Array<{ id: string; data: Record<string, unknown> }> = [];
  for (const raw of Array.isArray(data.tasks) ? data.tasks : []) {
    const row = raw as { id?: unknown; data?: unknown };
    if (typeof row.id !== 'string' || !row.id) continue;
    out.push({ id: row.id, data: (decodeJrStaffReadValue(row.data ?? {}) as Record<string, unknown>) ?? {} });
  }
  return out;
}

/** The jrStaffReads lane's cap on task ids per answers call. */
export const JR_STAFF_RESPONSE_TASKS_MAX = 30;

/**
 * The answers on to-dos her OTHER sign-in made or was given (every answer on
 * one it made, its own answer on one it was given), with Timestamps rebuilt.
 * Batched to the lane's cap.
 */
export async function loadJrStaffAliasTaskResponses(
  taskIds: readonly string[]
): Promise<Array<{ taskId: string; uid: string; data: Record<string, unknown> }>> {
  const ids = Array.from(new Set(taskIds.filter((id) => typeof id === 'string' && id)));
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += JR_STAFF_RESPONSE_TASKS_MAX) batches.push(ids.slice(i, i + JR_STAFF_RESPONSE_TASKS_MAX));
  const answers = await Promise.all(batches.map((batch) =>
    callJrStaffReads<{ responses?: unknown }>({ action: 'aliasTaskResponses', taskIds: batch })
  ));
  const out: Array<{ taskId: string; uid: string; data: Record<string, unknown> }> = [];
  for (const data of answers) {
    for (const raw of Array.isArray(data.responses) ? data.responses : []) {
      const row = raw as { taskId?: unknown; uid?: unknown; data?: unknown };
      if (typeof row.taskId !== 'string' || !row.taskId || typeof row.uid !== 'string' || !row.uid) continue;
      out.push({
        taskId: row.taskId,
        uid: row.uid,
        data: (decodeJrStaffReadValue(row.data ?? {}) as Record<string, unknown>) ?? {},
      });
    }
  }
  return out;
}

/**
 * "Mark mine done" on a to-do given to her OTHER sign-in (refute round on B2,
 * finding 6): the lane ticks the slot of the sign-in it was given to. A
 * refusal comes back in plain words.
 */
export async function completeJrStaffAliasTask(taskId: string): Promise<{ ok: boolean; already?: boolean }> {
  try {
    return await callJrStaffReads<{ ok: boolean; already?: boolean }>({ action: 'completeAliasTask', taskId });
  } catch (err) {
    const code = (err as { code?: unknown })?.code;
    if (code === 'too_many_requests') throw err;
    throw new Error(code === 'repeats'
      ? 'This to-do repeats. Mark it done on the sign-in it was given to, so its next one is made.'
      : 'Could not mark this done right now. Try again in a minute.');
  }
}

/**
 * "Remind me later" (or, with null, "Bring it back") on a to-do given to her
 * OTHER sign-in (second refute round on B2, MEDIUM 2): the lane writes that
 * sign-in's snooze slot.
 */
export async function snoozeJrStaffAliasTask(taskId: string, until: Date | null): Promise<void> {
  try {
    await callJrStaffReads<{ ok: boolean }>({ action: 'snoozeAliasTask', taskId, untilMs: until ? until.getTime() : null });
  } catch (err) {
    if ((err as { code?: unknown })?.code === 'too_many_requests') throw err;
    throw new Error('Could not quiet this to-do right now. Try again in a minute.');
  }
}

/**
 * The answer to an upload or poll to-do given to her OTHER sign-in (second
 * refute round on B2, MEDIUM 2): saved under that sign-in and ticked there,
 * the same as answering it on that sign-in. Links and picks only; a file is
 * attached on the sign-in the to-do was given to.
 */
export async function answerJrStaffAliasTask(
  taskId: string,
  answer: { kind: string; url: string; label: string; note?: string; selectedOptionIds?: string[] },
): Promise<void> {
  try {
    await callJrStaffReads<{ ok: boolean }>({
      action: 'answerAliasTask',
      taskId,
      answer: {
        kind: answer.kind,
        url: answer.url,
        label: answer.label,
        ...(answer.note ? { note: answer.note } : {}),
        ...(answer.selectedOptionIds?.length ? { selectedOptionIds: answer.selectedOptionIds } : {}),
      },
    });
  } catch (err) {
    const code = (err as { code?: unknown })?.code;
    if (code === 'too_many_requests') throw err;
    throw new Error(code === 'repeats'
      ? 'This to-do repeats. Answer it on the sign-in it was given to, so its next one is made.'
      : code === 'bad_request'
        ? 'That answer could not be saved. Check the link or your pick and try again.'
        : 'Could not save your answer right now. Try again in a minute.');
  }
}

/**
 * WHETHER A STUDENTS HOOK READS THROUGH THE LANE (final review on B2,
 * should-fix 2): only for a REAL Jr Staff ('ensemble' on her own users doc,
 * never a Director's view-as preview) whose ordinary roster read the rules
 * just refused on this sign-in. Any other refusal stays the error it always
 * was, so a family on a screen that tries the roster never calls the lane.
 */
export function studentsReadViaLane(input: {
  enabled: boolean;
  uid: string | null | undefined;
  refusedFor: string | null;
  realRole: string | null | undefined;
}): boolean {
  return input.enabled
    && !!input.uid
    && input.refusedFor === input.uid
    && input.realRole === 'ensemble';
}

/**
 * The signed-in person's REAL role, read once from her own users doc, only
 * after the rules refused a roster read (final review on B2). A one-shot get,
 * not a listener: the students hooks mount on many screens, and a role
 * listener in each would widen every screen's listener budget for a check
 * that only matters on a refusal. Firestore answers it from the users doc
 * listener the app already holds. Never the preview role. Null when it
 * cannot be read, which keeps the plain error and no lane call.
 */
export async function readRealRole(firestore: Firestore, uid: string): Promise<string | null> {
  try {
    const snap = await firestoreSdk.getDoc(firestoreSdk.doc(firestore, 'users', uid));
    const role = snap.exists() ? (snap.data() as { role?: unknown }).role : null;
    return typeof role === 'string' ? role : null;
  } catch {
    return null;
  }
}

export function isPermissionDenied(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null;
  return e?.code === 'permission-denied'
    || (typeof e?.message === 'string' && /missing or insufficient permissions/i.test(e.message));
}

/**
 * The merged family uids of one class's roster, for the recipient snapshot a
 * booking or a costume card stamps. Adult staff read the roster directly, as
 * before. A Jr Staff is refused that read by the rules now (B2), so on
 * exactly that refusal the answer comes from the jrStaffReads lane instead:
 * the same merged list, never per dancer.
 */
export async function classRosterFamilyUids(firestore: Firestore, offeringId: string): Promise<string[]> {
  try {
    const { collection, getDocs, query, where } = firestoreSdk;
    const snap = await getDocs(query(collection(firestore, 'students'), where('memberships', 'array-contains', offeringId)));
    return snap.docs.flatMap((d) => {
      const data = d.data() as { familyUids?: string[] };
      return Array.isArray(data.familyUids) ? data.familyUids : [];
    });
  } catch (err) {
    if (!isPermissionDenied(err)) throw err;
    return loadJrStaffClassFamilyUids(offeringId);
  }
}

/**
 * The same roster answer for a BOOKING (second refute round on B2, MEDIUM 1).
 * A Jr Staff may book a room for any class, but the lane lists families only
 * for a class that is hers. For any other class this answers `deferred`: the
 * booking is saved first, then stampJrStaffBookingAudience works the audience
 * out on the server for that booking and hands it back for its notice.
 */
export async function classRosterFamilyUidsForBooking(
  firestore: Firestore,
  offeringId: string,
): Promise<{ familyUids: string[]; deferred: boolean }> {
  try {
    return { familyUids: await classRosterFamilyUids(firestore, offeringId), deferred: false };
  } catch (err) {
    if ((err as { code?: unknown })?.code === 'not_your_class') return { familyUids: [], deferred: true };
    throw err;
  }
}

/**
 * The audience of ONE booking she made for a class that is not hers, worked
 * out and stamped on the booking by the jrStaffReads lane, returned only for
 * that booking's own notice.
 */
export async function stampJrStaffBookingAudience(reservationId: string): Promise<string[]> {
  const data = await callJrStaffReads<{ familyUids?: unknown }>({ action: 'stampBookingAudience', reservationId });
  return Array.isArray(data.familyUids)
    ? data.familyUids.filter((uid): uid is string => typeof uid === 'string' && !!uid)
    : [];
}

/** Test seam only. */
export function __resetJrStaffReadsForTests(): void {
  currentKey = '';
  snapshot = INITIAL;
  inflight = null;
  listeners.clear();
}

// ---------------------------------------------------------------------------
// N18: the colour her dancer home wears, for her email sign-in's home. One
// small lane call (two doc reads server side), cached for the session per uid,
// instead of the whole students list. `setJrStaffOwnColorCache` lets the
// Account picker publish a change the moment it saves.
// ---------------------------------------------------------------------------

export interface JrStaffOwnColor {
  studentId: string;
  color: string | null;
}

const ownColorCache = new Map<string, Promise<JrStaffOwnColor>>();
const ownColorListeners = new Set<() => void>();

export function loadJrStaffOwnColor(uid: string): Promise<JrStaffOwnColor> {
  let cached = ownColorCache.get(uid);
  if (!cached) {
    cached = callJrStaffReads<{ studentId?: unknown; color?: unknown }>({ action: 'ownColor' })
      .then((data) => ({
        studentId: typeof data.studentId === 'string' ? data.studentId : '',
        color: typeof data.color === 'string' && data.color ? data.color : null,
      }));
    // A failure is not cached, so the next home load tries again.
    cached.catch(() => { if (ownColorCache.get(uid) === cached) ownColorCache.delete(uid); });
    ownColorCache.set(uid, cached);
  }
  return cached;
}

export function setJrStaffOwnColorCache(uid: string, value: JrStaffOwnColor): void {
  ownColorCache.set(uid, Promise.resolve(value));
  for (const listener of Array.from(ownColorListeners)) listener();
}

export function subscribeJrStaffOwnColor(listener: () => void): () => void {
  ownColorListeners.add(listener);
  return () => { ownColorListeners.delete(listener); };
}
